import axiosInstanceNode from "@/api/axiosInstanceNode";

export const getCampaignSteps = async (campaignId) => {
  try {
    const response = await axiosInstanceNode.get(
      `/campaign/${campaignId}/sequence-steps`,
    );
    return response.data || [];
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to get campaign steps. Please try again.",
    );
  }
};

export const createCampaignStep = async (campaignId, stepData) => {
  try {
    const response = await axiosInstanceNode.post(
      `/campaign/${campaignId}/sequence-steps`,
      stepData,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to create step. Please try again.",
    );
  }
};

export const updateCampaignStep = async (campaignId, stepId, stepData) => {
  try {
    const response = await axiosInstanceNode.put(
      `/campaign/sequence-step/${stepId}`,
      stepData,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to update step. Please try again.",
    );
  }
};

export const deleteCampaignStep = async ({ stepId }) => {
  try {
    const response = await axiosInstanceNode.delete(
      `/campaign/sequence-step/${stepId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to delete step. Please try again.",
    );
  }
};

export const deleteIndividualSequenceStep = async ({ stepId, enrollmentId }) => {
  try {
    const response = await axiosInstanceNode.delete(
      `/campaign/individual-sequence-step/${stepId}`,
      { data: { enrollmentId } },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to delete individual sequence step. Please try again.",
    );
  }
};

export const reorderCampaignSteps = async (campaignId, stepsOrder) => {
  try {
    const response = await axiosInstanceNode.post(`/campaign/reorder-sequence`, {
      campaignId,
      steps: stepsOrder,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to reorder steps. Please try again.",
    );
  }
};

export const updateCampaignStepAdvanceSettings = async (
  stepId,
  advanceSettingsData,
) => {
  try {
    const response = await axiosInstanceNode.put(
      `/campaign/update-tone/${stepId}`,
      advanceSettingsData,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to update advance settings. Please try again.",
    );
  }
};

export const updateCampaignStepContentUserChanges = async ({
  stepId,
  payload,
}) => {
  try {
    const response = await axiosInstanceNode.put(
      `/campaign/sequence-template/${stepId}`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to update campaign step content changes. Please try again.",
    );
  }
};

// Learning from an edit runs in the background, so its failures stay silent.
const EDIT_LEARNING_REQUEST = { skipGlobalErrorToast: true };

/** After an AI step edit is saved: what the AI learned from it, or null. */
export const proposeEditLearning = async ({ templateId, originalSubject, originalContent }) => {
  const response = await axiosInstanceNode.post(
    `/campaign/sequence-template/${templateId}/edit-learning`,
    { originalSubject, originalContent },
    EDIT_LEARNING_REQUEST,
  );
  return response.data?.data ?? null;
};

export const confirmEditLearning = async (proposalId) => {
  const response = await axiosInstanceNode.post(
    `/campaign/edit-learning/${proposalId}/confirm`,
    {},
    EDIT_LEARNING_REQUEST,
  );
  return response.data?.data;
};

export const ignoreEditLearning = (proposalId) =>
  axiosInstanceNode.post(`/campaign/edit-learning/${proposalId}/ignore`, {}, EDIT_LEARNING_REQUEST);

/** Save a message edited for one lead (overrides the step's fixed message / campaign template). */
export const saveLeadMessage = async ({ enrollmentId, stepId, subject, content }) => {
  try {
    const response = await axiosInstanceNode.put(`/campaign/lead-message`, {
      enrollmentId,
      stepId,
      subject,
      content,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save the message for this lead. Please try again.",
    );
  }
};

/** Drop a lead-only edit so the fixed message / campaign template applies again. */
export const resetLeadMessage = async ({ enrollmentId, stepId }) => {
  try {
    const response = await axiosInstanceNode.delete(`/campaign/lead-message`, {
      data: { enrollmentId, stepId },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to reset the message for this lead. Please try again.",
    );
  }
};
